<template>
  <div class="bg-base-200 relative flex items-center gap-4 p-2">
    <div class="flex max-w-min grow items-center gap-2 overflow-hidden">
      <div v-if="sessionRole" class="avatar avatar-placeholder h-8">
        <!-- A QR code rather than the role's icon: this is the paired session,
             and the file sync role's arrows would read as a refresh button. -->
        <div class="h-8 w-8 rounded-full" :class="getCompanionRoleClass(sessionRole, 'bg')">
          <FontAwesomeIcon :icon="faQrcode" />
        </div>
      </div>
      <!-- The whole title block is the way back to the home screen: it is the
           one thing always on screen, and on a phone a wider target beats a
           second button competing for the same row. -->
      <component
        :is="sessionId ? RouterLink : 'div'"
        :to="sessionId ? { name: 'companionSessionRoot', params: { sessionId } } : undefined"
        class="flex flex-col overflow-hidden text-left whitespace-nowrap"
      >
        <span class="max-w-full overflow-hidden text-xs font-semibold text-ellipsis lg:text-sm">
          {{ title }}
        </span>
        <span
          v-if="session?.name"
          class="text-base-content/50 max-w-full overflow-hidden text-xs text-ellipsis"
        >
          {{ session.name }}
        </span>
      </component>
    </div>
    <RouterView name="navbar" v-slot="{ Component }">
      <component v-if="Component" :is="Component" class="grow" />
      <!-- Next to the session info the logo goes right rather than centred:
           the companion has no update button or anything else to claim that
           side, so centring would only leave a lopsided gap. -->
      <RouterLink
        v-else-if="session"
        :to="{ name: 'companionSessionRoot', params: { sessionId } }"
        class="ml-auto shrink-0"
      >
        <KonsolenkostLogo class="h-10" />
      </RouterLink>
      <DefaultNavbar
        v-else
        class="grow"
        :to="sessionId ? { name: 'companionSessionRoot', params: { sessionId } } : null"
      />
    </RouterView>
  </div>
</template>

<script setup lang="ts">
import { faQrcode } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { useRouteParams } from '@vueuse/router';
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { RouterLink, RouterView, useRoute } from 'vue-router';
import KonsolenkostLogo from '../../components/app/KonsolenkostLogo.vue';
import { useCompanionRoleInfo } from '../../composables/companion/useCompanionRoleInfo';
import DefaultNavbar from '../../views/navbar/DefaultNavbar.vue';
import { useCompanionSession } from '../stores/companionSessionStore';

const route = useRoute();
const { t, te } = useI18n();

const sessionId = useRouteParams('sessionId', '');
const { companion: session } = useCompanionSession(sessionId);

const { getCompanionRoleClass } = useCompanionRoleInfo('bg');

// A session can in principle carry several roles; the first one is what the
// session is for, so it stands in as the avatar.
const sessionRole = computed(() => session.value?.roles?.[0]);

// Not every companion route carries a title key, so fall back to an empty
// slot rather than leaking the raw i18n path.
const title = computed(() => {
  const key = `routes.${route.name?.toString()}.title`;

  return te(key) ? t(key) : '';
});
</script>
